import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCopyValue } from '../../../../hooks'
import { ConfirmationModal } from '../../../Confirmation'
import { Divider } from '../../../Divider'
import { useProfileContactsWidgetListCellHandlers } from '../ProfileContactsWidget.hooks'
import { ProfileContactsWidgetListCellEdit } from '../ProfileContactsWidgetListCellEdit'
import { ProfileContactsWidgetListCellView } from '../ProfileContactsWidgetListCellView'

import {
  PROFILE_CONTACTS_WIDGET_NOTIFICATION_KEY,
  PROFILE_CONTACTS_WIDGET_VALUE_FIELD,
} from './ProfileContactsWidgetListCell.constants'
import { getContactCellData } from './ProfileContactsWidgetListCell.utils'

import * as styles from './ProfileContactsWidgetListCell.module.css'

function ProfileContactsWidgetListCell({
  api,
  canEdit,
  contact,
  editingContactId,
  editingIsDirty,
  onClickEmail,
  onDirtyChange,
  onEdited,
  onEditRequest,
  onSuccess,
}) {
  const { t } = useTranslation()

  const { canDelete, canEdit: contactCanEdit, id, typeConfig, value } = contact

  const isEditable = canEdit && contactCanEdit

  const {
    formattedValue,
    infoTooltipKey,
    initialEditValue,
    label,
    link,
    noValueText,
    target,
    tooltip,
  } = getContactCellData({ t, typeConfig, value })

  const [modal, setModal] = useState(null)
  const [isDeleteValue, setIsDeleteValue] = useState(false)

  const { copied, handleCopy } = useCopyValue({
    successNotificationKey: PROFILE_CONTACTS_WIDGET_NOTIFICATION_KEY.copied,
    value: formattedValue,
  })

  const { handleConfirmDelete, handleEditSubmit } =
    useProfileContactsWidgetListCellHandlers({
      api,
      contact,
      setIsDeleteValue,
      setModal,
      typeConfig,
      onEdited,
      onEditRequest,
      onSuccess,
    })

  function handleClickEmail() {
    onClickEmail({ type: typeConfig.type, value })
  }

  const handleClickValue =
    onClickEmail && typeConfig.isEmail && value ? handleClickEmail : null

  function handleEdit() {
    onEditRequest(id)
  }

  function handleCancelEdit() {
    onEditRequest(null)
    setModal(null)
    setIsDeleteValue(false)
  }

  function handleCloseModal() {
    setModal(null)
  }

  function handleSubmitAttention() {
    onEditRequest(id)
    setModal(null)
  }

  function handleOpenModalDelete() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DeleteBtnKey'),
        }}
        description={t(
          'ProfileContactsWidget.ConfirmationModalDescriptionTextKey'
        )}
        title={t('Modal.DeleteModalTitlekey')}
        onCancel={handleCloseModal}
        onSubmit={handleConfirmDelete}
      />
    )
  }

  function handleOpenAttention() {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          cancelText: t('Modal.CommonGoBackBtnKey'),
          submitText: t('Modal.CancelBtnKey'),
        }}
        description={t(
          'ProfileContactsWidget.AttentionModalDescriptionTextKey'
        )}
        title={t('Common.AttentionModalTitleKey')}
        onCancel={handleCloseModal}
        onSubmit={handleSubmitAttention}
      />
    )
  }

  function handleSomeEdits() {
    if (editingContactId) {
      editingIsDirty ? handleOpenAttention() : handleSubmitAttention()
    } else {
      handleEdit()
    }
  }

  return (
    <div className={styles.cell}>
      {isEditable && editingContactId === id ? (
        <ProfileContactsWidgetListCellEdit
          canDelete={canDelete}
          infoTooltipKey={infoTooltipKey}
          initialValues={{ value: initialEditValue }}
          isDeleteValue={isDeleteValue}
          label={label}
          name={PROFILE_CONTACTS_WIDGET_VALUE_FIELD}
          qaId={id}
          typeConfig={typeConfig}
          onClick={handleCancelEdit}
          onDelete={handleOpenModalDelete}
          onDirtyChange={onDirtyChange}
          onSubmit={handleEditSubmit}
        />
      ) : (
        <ProfileContactsWidgetListCellView
          canEdit={isEditable}
          floatingTooltip={tooltip}
          infoTooltipKey={infoTooltipKey}
          isCopied={copied}
          label={label}
          link={link}
          noValueText={noValueText}
          qaId={id}
          target={target}
          value={formattedValue}
          onClickValue={handleClickValue}
          onCopy={handleCopy}
          onEdit={handleSomeEdits}
        />
      )}
      {modal}

      <Divider className={styles.divider} />
    </div>
  )
}

export default ProfileContactsWidgetListCell
